import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ArrowDropDownRounded as ArrowDropDownRoundedIcon } from '@mui/icons-material';

const TeamDropdownOptionsModal = ({ open, onClose, anchorEl, offset = 10, firstModalLeft, firstModalTop, onSelectFieldType }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (open && firstModalLeft !== undefined && firstModalTop !== undefined) {
            const calculatePosition = () => {
                const modalWidth = 200; // Same as first modal
                const gap = 10; // Gap between modals

                let top = firstModalTop;
                let left = firstModalLeft + modalWidth + gap;

                // Adjust if modal would go off right side of screen
                if (left + modalWidth > window.innerWidth) {
                    left = firstModalLeft - modalWidth - gap; // Show on left side instead
                }

                setModalPosition({ top, left });
            };

            calculatePosition();
            window.addEventListener('resize', calculatePosition);
            return () => window.removeEventListener('resize', calculatePosition);
        }
    }, [open, firstModalLeft, firstModalTop, isMobile]);

    if (!open) return null;

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        onClose();
    };

    const iconStyles = {
        color: '#828282',
        fontSize: '2rem', // Bigger size to match container
        marginRight: isMobile ? '0.5rem' : '0.625rem',
    };

    const dropdownOptions = [
        {
            type: 'custom',
            label: 'Custom Dropdown',
            icon: <ArrowDropDownRoundedIcon />
        }
    ];

    const presets = [
        {
            type: 'countries',
            label: 'List of countries',
            icon: <ArrowDropDownRoundedIcon />
        },
        {
            type: 'states',
            label: 'List of states',
            icon: <ArrowDropDownRoundedIcon />
        }
    ];

    const handleOptionClick = (type) => {
        if (type === 'custom') {
            // When "Custom Dropdown" is clicked, add a dropdown field
            if (onSelectFieldType) {
                onSelectFieldType('dropdown');
            }
            onClose(); // Close the dropdown options modal
        } else {
            // For presets (countries, states), will be implemented later
            console.log('Selected dropdown option:', type);
        }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1200,
                backgroundColor: 'transparent',
            }}
            onClick={handleOverlayClick}
        >
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    position: 'absolute',
                    top: `${modalPosition.top}px`,
                    left: `${modalPosition.left}px`,
                    backgroundColor: '#fff',
                    borderRadius: '0.75rem',
                    width: isMobile ? 'calc(100% - 2rem)' : '90%',
                    maxWidth: '12.5rem',
                    boxShadow: '0 0.25rem 1rem rgba(0, 0, 0, 0.08), 0 0.125rem 0.5rem rgba(0, 0, 0, 0.04)',
                    margin: isMobile ? '0 1rem' : 0,
                    outline: 'none',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Custom Dropdown Option */}
                    {dropdownOptions.map((option, index) => (
                        <Box
                            key={option.type}
                            onClick={() => handleOptionClick(option.type)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: isMobile ? '0.5rem 0.75rem' : '0.625rem 1rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                borderRadius: '0.5rem',
                                marginBottom: index < dropdownOptions.length - 1 ? '0.25rem' : '0.5rem',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                            }}
                        >
                            {React.cloneElement(option.icon, { sx: iconStyles })}
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#333',
                                }}
                            >
                                {option.label}
                            </Typography>
                        </Box>
                    ))}

                    {/* PRESETS Label */}
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: isMobile ? '0.7rem' : '0.75rem',
                            color: '#828282',
                            textTransform: 'uppercase',
                            padding: isMobile ? '0.5rem 0.75rem' : '0.625rem 1rem',
                            paddingTop: '0.75rem',
                            paddingBottom: '0.25rem',
                        }}
                    >
                        PRESETS
                    </Typography>

                    {/* Preset Options */}
                    {presets.map((preset, index) => (
                        <Box
                            key={preset.type}
                            onClick={() => handleOptionClick(preset.type)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: isMobile ? '0.5rem 0.75rem' : '0.625rem 1rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                borderRadius: '0.5rem',
                                marginBottom: index < presets.length - 1 ? '0.25rem' : 0,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                            }}
                        >
                            {React.cloneElement(preset.icon, { sx: iconStyles })}
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#333',
                                }}
                            >
                                {preset.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default TeamDropdownOptionsModal;

